import useCatchHandler from "@/services/catchErrorHandler"
import useOAuth from "@/services/oauth"
import { useStore } from "@/store"
import { ActionTypes as AssessmentActions } from "@/store/modules/assessment/actions"
import { ActionTypes as GlobalActions } from "@/store/modules/global/actions"
import { ActionTypes as AuthActions } from "@/store/modules/auth/actions"
import axios from "axios"
import { computed } from "vue"
import { EXHAUSTED_THE_LIMIT, SUPPORT_EMAIL } from "../core/constants"
// @ts-ignore
import { toast } from "/src/plugins/vue3-toastify.plugin"
import i18n from "@/plugins/i18n"

export default function useApiAuth() {
    const { handleError } = useCatchHandler()
    const { API_PREFIX } = useOAuth()
    const store = useStore()
    const getApiRequestHeader = computed(
        () => store.getters.getApiRequestHeader
    )
    const getAuthUserEmail = computed(() => store.getters.getAuthUser?.email)
    const getUserCountryCodeIP = computed(
        () => store.getters.getCurrentUserCountryDialCode
    )
    const getUserTimezone = computed(() => store.getters.getCurrentUserTimezone)
    const apiRegisterUser = async (
        provider: string = "email",
        email: string,
        phoneExt: number | null,
        phone: number | null,
        firstName: string,
        lastName: string,
        userJobTitle: string,
        workspaceName: string,
        workspaceUrl: string,
        isTermOfUseAccepted: boolean,
        isPromotionalEmailAccepted: boolean,
        utmSource: string,
        utmMedium: string,
        utmCampaign: string,
        utmTerm: string,
        utmContent: string,
        language: string,
        accessKey?: string, // accessKey will be passed as null when this method is called from the user with invitation (InviteeRegistrationPage.vue)
        inviteKey?: string,
        timeZone?: string
    ) => {
        let key = {}
        // accessKey will be passed as null when this method is called from the user with invitation (InviteeRegistrationPage.vue)
        if (inviteKey) {
            key = { inviteKey }
        } else {
            // accessKey will be passed as non-null when this method is called from the (CreateWorkspaceForm.vue)
            key = { accessKey }
        }
        phoneExt =
            phoneExt ||
            (getUserCountryCodeIP.value
                ? Number(getUserCountryCodeIP.value)
                : phoneExt)

        timeZone = getUserTimezone.value
            ? getUserTimezone.value
            : "Asia/Kolkata"

        if (getAuthUserEmail.value == SUPPORT_EMAIL) {
            phoneExt = 1
        }

        let campaignSource: Record<string, any> | null = null
        const storedTrackingData = localStorage.getItem("ad_tracking")

        if (storedTrackingData) {
            try {
                campaignSource = JSON.parse(storedTrackingData)
            } catch (error) {
                campaignSource = null
            }
        }

        try {
            const body = {
                provider,
                email,
                phoneExt,
                phone,
                firstName,
                lastName,
                userJobTitle,
                workspaceName,
                workspaceUrl,
                isTermOfUseAccepted,
                isPromotionalEmailAccepted,
                utmSource,
                utmMedium,
                utmCampaign,
                utmTerm,
                utmContent,
                language,
                ...key,
                domainName: window.location.hostname,
                timeZone,
                campaignSource,
            }

            const response = await axios.post(
                `${API_PREFIX}/public/auth/registration`,
                body
            )

            if (response.status.toString().startsWith("2")) {
                localStorage.removeItem("ad_tracking")
            }

            return response
        } catch (err) {
            const { statusCode } = err.response!.data!.error
            // 400 denotes the error where namespace is already taken, that we need to show under the workspaceUrl field,
            // & not in notification bar
            if (
                [
                    "Workspace Name Taken",
                    "Namespace Taken",
                    "Record exist",
                ].includes(err.response.data.error.message)
            ) {
                return 403
            }
            if (err.code === "ERR_NETWORK") {
                toast.error(EXHAUSTED_THE_LIMIT)
            }
            handleError(err.response)
            return -1
        }
    }

    const apiRegisterUserVerifyOtp = async (
        provider: string = "email",
        email: string,
        otp: number
    ) => {
        try {
            const body = {
                provider,
                email,
                otp,
                domainName: window.location.hostname,
            }
            return await axios.post(
                `${API_PREFIX}/public/auth/otp/verify`,
                body
            )
        } catch (err) {
            if (err.code === "ERR_NETWORK") {
                toast.error(EXHAUSTED_THE_LIMIT)
            }
            return err.response.data
        }
    }

    const apiRegisterUserSendOtp = async (
        provider: string = "email",
        email: string,
        action: string,
        resendCode: boolean = false,
        firstName: string = "",
        lastName: string = "",
        recaptchaToken: string,
        language: string,
        phone: number,
        phoneExt: number
    ) => {
        try {
            const body = {
                provider,
                email,
                action,
                resendCode,
                firstName,
                lastName,
                recaptchaToken,
                language,
                domainName: window.location.hostname,
                phone,
                phoneExt,
            }
            return await axios.post(`${API_PREFIX}/public/auth/otp`, body)
        } catch (err) {
            if (err.code === "ERR_NETWORK") {
                toast.error(EXHAUSTED_THE_LIMIT)
            }
            return err.response.data
        }
    }

    const apiLoginUser = async (
        provider: string = "email",
        email: string,
        accessKey: string,
        workspaceUrl: string,
        isRecoveryCode: boolean,
        totp: string
    ) => {
        try {
            const response = await axios.post(
                `${API_PREFIX}/public/auth/login`,
                {
                    provider,
                    email,
                    accessKey,
                    workspaceUrl,
                    domainName: window.location.hostname,
                    timeZone: getUserTimezone.value
                        ? getUserTimezone.value
                        : "Asia/Kolkata",
                    isRecoveryCode,
                    totp,
                }
            )
            return response
        } catch ({ response }) {
            if (response.code === "ERR_NETWORK") {
                toast.error(EXHAUSTED_THE_LIMIT)
            }
            handleError(response)
            return -1
        }
    }

    const apiRegisterWorkspace = async (
        name: string,
        workspaceUrl: string,
        userJobTitle: string
    ) => {
        try {
            const body = {
                name,
                workspaceUrl,
                userJobTitle,
            }
            return await axios.post(`${API_PREFIX}/workspace/register`, body)
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiAddNewWorkspace = async (
        provider: string = "email",
        email: string,
        phoneExt: number = 91,
        phone: number = null,
        firstName: string,
        lastName: string,
        userJobTitle: string,
        workspaceName: string,
        workspaceUrl: string,
        isTermOfUseAccepted: boolean,
        isPromotionalEmailAccepted: boolean,
        language: string
    ) => {
        phoneExt = getUserCountryCodeIP.value
            ? Number(getUserCountryCodeIP.value)
            : 91

        if (getAuthUserEmail.value == SUPPORT_EMAIL) {
            phoneExt = 1
        }
        try {
            const body = {
                provider,
                email,
                phoneExt,
                phone,
                firstName,
                lastName,
                userJobTitle,
                workspaceName,
                workspaceUrl,
                isTermOfUseAccepted,
                isPromotionalEmailAccepted,
                language,
                domainName: window.location.hostname,
            }
            return await axios.post(
                `${API_PREFIX}/workspace/create`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            const { statusCode } = response!.data!.error
            // 409 denotes the error where namespace is already taken, that we need to show under the workspaceUrl field,
            // & not in notification bar
            if (statusCode === 409) {
                return 409
            }
            if (statusCode === 400) {
                return 400
            }
            if (statusCode === 403) {
                return 403
            }
            handleError(response)
            return -1
        }
    }

    const apiWorkspaceList = async () => {
        try {
            return await axios.get(`${API_PREFIX}/workspace/list`)
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    // the permanent workspace session token is received in the response of this api
    const apiWorkspaceLogin = async (
        workspaceKey: string,
        workspaceUrl: string,
        isRecoveryCode: boolean,
        totp: string = ""
    ) => {
        try {
            const body = {
                workspaceKey,
                workspaceUrl,
                isRecoveryCode,
                totp,
            }
            return await axios.post(
                `${API_PREFIX}/public/auth/workspace/login`,
                body
            )
        } catch (err) {
            if (err.code === "ERR_NETWORK") {
                toast.error(EXHAUSTED_THE_LIMIT)
            }
            handleError(err.response)
            return -1
        }
    }

    const calculatePercentage = (done: number, total: number) => {
        if (total === 0) {
            return 0 // Avoid division by zero
        }
        const percentage = (done / total) * 100
        return Math.round(percentage)
    }

    const apiUserProfileSummary = async () => {
        try {
            const store = useStore()
            let response: any = await axios.get(
                `${API_PREFIX}/profile/summary`,
                getApiRequestHeader.value
            )
            store.dispatch(
                GlobalActions.SET_DISCOVER_PERCENTAGE,
                calculatePercentage(
                    response?.data?.discover?.doneCount ?? 0,
                    response?.data?.discover?.totalCount ?? 0
                )
            )
            const userRoleId = response.data.userWorkspaceProfile?.userRoleId
            store.dispatch(AuthActions.SET_USER_ROLE_ID, userRoleId)
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiWorkspaceDetails = async () => {
        try {
            let response: any = await apiUserProfileSummary()
            const email = getAuthUserEmail.value
            const isSpecialUser =
                email.includes("hnrtech") || email.includes("testlify")
            const optionLimit = isSpecialUser
                ? 9999
                : response.organization?.workSpaceLimit?.optionAdded
            const questionLimit = isSpecialUser
                ? 9999
                : response.organization?.workSpaceLimit?.questionAdded

            store.dispatch(AssessmentActions.UPDATE_OPTION_LIMIT, optionLimit)
            store.dispatch(
                AssessmentActions.UPDATE_QUESTION_LIMIT,
                questionLimit
            )
            return response.organization
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiCandidateLabels = async (
        query: string,
        limit: number,
        skip: number,
        filter: string,
        inOrder: string
    ) => {
        try {
            let response: any = await axios.get(
                `${API_PREFIX}/workspace/label?limit=${limit}&skip=${skip}&query=${query}&filter=${filter}&inOrder=${inOrder}`,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiCreateCandidateLabel = async (
        title: string,
        labelType: string[]
    ) => {
        try {
            const body = { title, labelType }
            let response: any = await axios.post(
                `${API_PREFIX}/workspace/label`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            if (response?.data?.error?.statusCode === 400) {
                toast.error(
                    `${i18n.global.t(
                        "candidate_labels.toast_notifications.label_duplicated"
                    )}`
                )
            } else {
                handleError(response)
            }
            return -1
        }
    }

    const apiUpdateAssessmentLabel = async (id: string, title: string) => {
        try {
            const body = { id, title }
            let response: any = await axios.post(
                `${API_PREFIX}/assessment/label/${id}`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiUpdateCandidateLabel = async (
        id: string,
        title: string,
        labelType: string[]
    ) => {
        try {
            const body = { id, title, labelType }
            let response: any = await axios.put(
                `${API_PREFIX}/workspace/label/${id}`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiDeleteCandidateLabel = async (id: string) => {
        try {
            let response: any = await axios.delete(
                `${API_PREFIX}/workspace/label/${id}`,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiUserProfileEdit = async (body: {
        firstName?: string
        lastName?: string
        phoneExt?: number
        phone?: number
        defaultLanguage?: string
        accessibilitySettings?: boolean
        timeZone?: string
    }) => {
        try {
            const response: any = await axios.put(
                `${API_PREFIX}/workspace/user/profile`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiUpdateWorkspaceAppearance = async (
        brandColor: string,
        logo: string,
        favicon: string,
        customImage: string,
        brandTextColor: string = ""
    ) => {
        try {
            const body = {
                logo: logo ? logo : "",
                favicon: favicon ? favicon : "",
                brandColor: brandColor ? brandColor : "",
                customImage: customImage ? customImage : "",
                brandTextColor,
            }
            let response: any = await axios.put(
                `${API_PREFIX}/workspace/profile/appearance`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiDeleteWorkspace = async () => {
        try {
            let response: any = await axios.delete(
                `${API_PREFIX}/workspace`,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiUpdateWorkspaceName = async (name: string) => {
        try {
            const body = {
                name,
            }
            let response: any = await axios.put(
                `${API_PREFIX}/workspace/profile`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiExtendTrial = async () => {
        try {
            let response: any = await axios.put(
                `${API_PREFIX}/workspace/profile/extendtrial`,
                null,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiUpdateNotificationEmails = async (
        value: boolean,
        adminValue: boolean
    ) => {
        try {
            const body = {
                candidateNotificationAllowed: value,
                administratorNotificationAllowed: adminValue,
            }
            let response: any = await axios.put(
                `${API_PREFIX}/workspace/profile`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    // getApiRequestHeader is purposely not used as request header
    const apiSwitchWorkspace = async (
        sessionCookie: string,
        limit: number,
        skip: number,
        query: string,
        isActive: boolean
    ) => {
        const requestHeaders = {
            headers: {
                Authorization: `Bearer ${sessionCookie}`,
            },
        }
        try {
            let response: any = await axios.get(
                `${API_PREFIX}/workspace/switch?limit=${limit}&skip=${skip}&query=${encodeURIComponent(
                    query
                )}&isActive=${isActive}`,
                requestHeaders
            )
            return response.data
        } catch ({ response }) {
            const errors: any = response!.data!.error
            const { statusCode } = errors
            // token expired
            if (statusCode === 401) {
                return 401
            }
            handleError(response)
            return -1
        }
    }

    const apiGetCountryCode = async (getIpAddress) => {
        try {
            return await axios.get(
                `${API_PREFIX}/public/user/country?ip=${getIpAddress}`
            )
        } catch (err) {
            if (err.code === "ERR_NETWORK") {
                toast.error(EXHAUSTED_THE_LIMIT)
            }
            handleError(err.response)
            return -1
        }
    }
    const apiGenerateApiKey = async () => {
        try {
            return await axios.post(
                `${API_PREFIX}/workspace/apikey/generate`,
                {},
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiDeleteApiKey = async () => {
        try {
            return await axios.delete(
                `${API_PREFIX}/workspace/apikey`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiSocialLogin = async (
        isRegister: boolean,
        code: string,
        redirectUri: string,
        state: string,
        provider: string
    ) => {
        try {
            const body = {
                code,
                redirectUri,
                state,
                provider,
                isRegister,
            }
            const response: any = await axios.post(
                `${API_PREFIX}/public/auth/social`,
                body
            )
            return response
        } catch (err) {
            if (err.code === "ERR_NETWORK") {
                toast.error(EXHAUSTED_THE_LIMIT)
            }
            return err.response.data
        }
    }
    const apiPhoneVerificationExt = async () => {
        try {
            let response: any = await axios.get(
                `${API_PREFIX}/public/auth/number/verify`
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiUserPhoneNumberOtp = async (
        phoneNumber: string,
        channel: string
    ) => {
        try {
            let response: any = await axios.get(
                `${API_PREFIX}/public/auth/phonenumber/otp?phoneNumber=${phoneNumber}&channel=${channel}`
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiUserPhoneNumberVerify = async (
        phoneNumber: string,
        code: string,
        email: string
    ) => {
        const body = {
            phoneNumber,
            code,
            email,
        }
        try {
            let response: any = await axios.post(
                `${API_PREFIX}/public/auth/phonenumber/otp`,
                body
            )
            return response
        } catch ({ response }) {
            return response
        }
    }
    const apiMobileVerificationRemindMeLater = async (orgId: string) => {
        try {
            const body = {
                orgId,
            }
            return await axios.patch(
                `${API_PREFIX}/profile/reminder`,
                body,
                getApiRequestHeader.value
            )
        } catch (err) {
            console.log("Error, ")
            return err
        }
    }

    return {
        apiRegisterUser,
        apiLoginUser,
        apiRegisterWorkspace,
        apiCreateCandidateLabel,
        apiUpdateAssessmentLabel,
        apiUpdateCandidateLabel,
        apiDeleteCandidateLabel,
        apiWorkspaceList,
        apiRegisterUserSendOtp,
        apiRegisterUserVerifyOtp,
        apiWorkspaceLogin,
        apiUserProfileSummary,
        apiUserProfileEdit,
        apiUpdateWorkspaceAppearance,
        apiUpdateWorkspaceName,
        apiExtendTrial,
        apiDeleteApiKey,
        apiUpdateNotificationEmails,
        apiDeleteWorkspace,
        apiSwitchWorkspace,
        apiCandidateLabels,
        apiGetCountryCode,
        apiGenerateApiKey,
        apiWorkspaceDetails,
        apiSocialLogin,
        apiAddNewWorkspace,
        apiPhoneVerificationExt,
        apiUserPhoneNumberOtp,
        apiUserPhoneNumberVerify,
        apiMobileVerificationRemindMeLater,
    }
}
